import { useCallback, useEffect, useMemo, useState } from "react";
import { resolveLocalized } from "@shared/i18n";
import type { LocalizedString } from "@shared/types/foyer";
import { getBinding, getScreenId } from "../lib/binding";
import { deadline, FETCH_TIMEOUT_MS } from "../lib/boundedFetch";

/**
 * What this room may watch, for the television (`TV-3`).
 *
 * The read half of Foyer's own streaming client. Modelled line for line on
 * `useRoomOffers`: a bare `fetch`, the screen token from the pairing binding, a
 * poll, last-good retained on failure, and `undefined` for "not answered yet".
 * There is no central API client in this app and this file does not introduce
 * one — every call in `src/tv` is a bare fetch, and consistency with the eleven
 * that already exist is worth more here than a wrapper.
 *
 * ## Why the catalogue is its own request rather than a block of the config
 *
 * Same reason offers are. A scheme binding is edited by an operator and pushed
 * over the realtime channel; a catalogue changes when a package is granted, a
 * licence window opens, or a title is withdrawn — none of which anybody touches
 * the config for. Folding rails into the config would mean either a stale
 * library or a config refetch for every premise, catalogue or not.
 *
 * ## ⚠️ The empty case is the COMMON case today
 *
 * Foyer production has **zero `packages` rows**. Until a premise is
 * granted a package every rail resolves to nothing, so "the library is empty"
 * is not an edge case to hide behind a spinner — it is what most premises will
 * answer, and the screen has to say so in words. {@link TvCatalogueRead} is
 * shaped to keep "not answered", "answered with nothing" and "could not be
 * reached" apart, because those want three different sentences.
 *
 * ## Tolerance is deliberate, and it is not paranoia
 *
 * The routes (`/tv/home`, `/tv/subpages`, `/tv/rail/:carouselId`,
 * `/tv/title/:assetId`) are being built alongside this screen. Every normaliser
 * below IGNORES a field it does not know and DEGRADES on one that is missing,
 * rather than throwing: a television that renders nothing because the server
 * grew a field is a worse failure than one that renders a title with no year.
 *
 * The alias lists are not guesses either. They cover the two shapes this data
 * demonstrably takes today — the flattened TV shape the route is expected to
 * send, and the raw `carouselResolver` / content-API shape it is built from
 * (`{id, title: [{language,text}], assets: Asset[]}`, assets carrying
 * `assetId`, `originalTitle`, `localizedInfo[]`, `productionYear`, `images[]`).
 * If the route passes those through unflattened, this still renders.
 */

/** How often to re-ask for the home read. */
export const TV_CATALOGUE_POLL_MS = 10 * 60_000;

/** Backoff for an API that is not answering. Same shape as `useRoomOffers`'. */
const RETRY_BASE_MS = 5_000;
const RETRY_MAX_MS = 60_000;

/**
 * Failures before the read is declared unreachable.
 *
 * It keeps retrying afterwards — this only decides when the SCREEN is allowed
 * to stop saying "loading" and start saying "could not be loaded". Three
 * attempts at 5 s / 10 s / 20 s is about thirty-five seconds, which is longer
 * than a hotel Wi-Fi hiccup and shorter than a guest's patience with a spinner.
 */
const UNREACHABLE_AFTER = 3;

// ── what the screen renders ─────────────────────────────────────────────────

export type TvTitleKind = "movie" | "series" | "episode" | "other";

/** One tile on a rail. The least a poster needs to be drawn and pressed. */
export interface TvCatalogueItem {
  assetId: string;
  title: string;
  /** A second line — a series name on an episode, a tagline on a film. */
  subtitle?: string;
  imageUrl?: string;
  kind: TvTitleKind;
  year?: number;
  durationSeconds?: number;
}

/**
 * One rail, and the page of it we currently hold.
 *
 * `hasMore` is the whole point of the shape. A rail must NOT ship its entire
 * set on the home read — a premise with a thousand titles would make the first
 * screen of the evening the slowest one — so the home read carries page 1 and
 * the rest is fetched as focus approaches the end.
 */
export interface TvRail {
  carouselId: string;
  title: string;
  items: TvCatalogueItem[];
  /** 1-based, matching `?page=N`. */
  page: number;
  /** Whether a further page is believed to exist. */
  hasMore: boolean;
}

/**
 * A named sub-page of the library (`/tv/subpages`) — "Barn", "Nyheter".
 *
 * 🚨 It carries its OWN RAILS, and they arrive with the page.
 *
 * `/tv/subpages` returns `{id, name, title, icon, rails}` per page — the server
 * has resolved each page's rails for this room already, by the same
 * `tvHomeRails` the home read uses. This type used to be `{subPageId, title}`
 * and silently dropped the rest, so a client could name a page and had no way
 * to draw it. A menu that opens onto nothing is worse than no menu.
 */
export interface TvSubPage {
  subPageId: string;
  title: string;
  /** Already `replace("_", "-")`d by the server — see `subPageIconForApp`. */
  icon: string | null;
  /**
   * What this page draws, in order.
   *
   * NOT pageable: `tvSubPages` calls `tvHomeRails` with `pageable: false`, so
   * every rail here arrives whole and `hasMore` is false. A "see all" inside a
   * sub-page has no route to ask for page 2.
   */
  rails: TvRail[];
}

/**
 * Why a surface is empty, when it is — the SERVER's own verdict.
 *
 * 🚨 Carried, never re-derived. `tvHomeEmptyState` distinguishes four causes
 * (no room type, nothing selected, every selection dangling, nothing available
 * to this room) and each has a different fix. The client used to parse none of
 * them, so a room with no content reached `TvShell` as a bare
 * `sections.length === 0` and got one invented sentence — "no TV is connected
 * to this room yet" — which blames the PAIRING for a content problem and sends
 * whoever reads it to check the wrong thing.
 */
export interface TvEmptyState {
  code: string;
  /** Operator-facing, and long. Never shown to a guest. */
  hint: string;
  authoredRails?: number;
  visibleRails?: number;
  danglingRails?: number;
}

export interface TvCatalogueHome {
  rails: TvRail[];
  subPages: TvSubPage[];
  /** Absent when something drew. See {@link TvEmptyState}. */
  emptyState?: TvEmptyState;
}

export interface TvEpisode extends TvCatalogueItem {
  episodeNumber?: number;
  seasonNumber?: number;
}

export interface TvSeason {
  seasonId: string;
  /** Season number as authored. Absent on a catalogue that only names them. */
  number?: number;
  title?: string;
  episodes: TvEpisode[];
}

export interface TvTitleDetail {
  assetId: string;
  title: string;
  kind: TvTitleKind;
  synopsis?: string;
  imageUrl?: string;
  /** A wide still, when the catalogue has one. Falls back to `imageUrl`. */
  backdropUrl?: string;
  year?: number;
  durationSeconds?: number;
  genres: string[];
  /** Parental rating as a bare label — never re-interpreted by this client. */
  rating?: string;
  /** Empty for anything that is not a series. */
  seasons: TvSeason[];
  /**
   * The supplier's credit (`CONTENT-26`), in their own words. Absent for
   * Nenda's own titles and on a care premise - the server decides both.
   */
  credit?: { name: string; credits?: string; copyright?: string };
}

/** An HTTP answer that was not 2xx. The status is load-bearing for playback. */
export class TvApiError extends Error {
  constructor(readonly status: number) {
    super(`tv api ${status}`);
    this.name = "TvApiError";
  }
}

/**
 * Which language to read a catalogue string in.
 *
 * Threaded rather than assumed because the catalogue is genuinely multilingual
 * and the television already knows the guest's choice. `defaultLocale` is the
 * premise's own, exactly as `resolveLocalized` uses it everywhere else.
 */
export interface TvLocale {
  locale: string;
  defaultLocale: string;
}

const EN: TvLocale = { locale: "en", defaultLocale: "en" };

// ── tolerant readers ────────────────────────────────────────────────────────

function rec(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" ? (v as Record<string, unknown>) : {};
}

function str(v: unknown): string | undefined {
  return typeof v === "string" && v.trim() ? v.trim() : undefined;
}

function num(v: unknown): number | undefined {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  // Numbers arrive as strings from more than one upstream. Parsing them once
  // here rather than at six call sites is the difference between "2019"
  // rendering and a blank where a year should be.
  if (typeof v === "string" && v.trim() && Number.isFinite(Number(v))) {
    return Number(v);
  }
  return undefined;
}

function list(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

/**
 * A catalogue string, in the guest's language, from any of the three shapes it
 * actually takes.
 *
 * 1. a plain `string` — a route that already flattened;
 * 2. a `LocalizedString` map (`{sv, en}`) — the shape the rest of Foyer speaks;
 * 3. `[{language, text}]` / `[{language, title}]` — what `CarouselTitle` and
 *    the content API's `localizedInfo` are.
 *
 * Shape 3 is folded into shape 2 and handed to the same `resolveLocalized`
 * every other screen uses, so the fallback chain (active locale → premise
 * default → any populated one) is one implementation rather than two that
 * disagree the first time a hotel adds a third language.
 */
export function localizedText(
  v: unknown,
  loc: TvLocale,
  ...fields: string[]
): string | undefined {
  if (typeof v === "string") return str(v);
  if (Array.isArray(v)) {
    const map: Record<string, string> = {};
    for (const entry of v) {
      const o = rec(entry);
      const language = str(o.language) ?? str(o.locale);
      const text = firstField(o, ["text", "title", "value", ...fields]);
      if (language && text) map[language] = text;
    }
    return resolveLocalized(map, loc.locale, loc.defaultLocale) || undefined;
  }
  if (v && typeof v === "object") {
    // ⚠️ SIFTED to strings before it goes anywhere near `resolveLocalized`.
    //
    // That function assumes string values throughout — `active.trim()`,
    // `fallback.trim()`, and `.trim()` over every `Object.values` entry — so a
    // nested value (`{sv: {short, long}}`, which is exactly the sort of shape
    // the tolerance here exists to survive) would raise a TypeError. And this
    // runs inside a `useMemo` DURING RENDER, so that TypeError does not degrade
    // one title: it takes the whole TV app into `SelfHealBoundary`. A
    // normaliser that can crash the app is not a tolerant normaliser.
    return (
      resolveLocalized(stringsOnly(v), loc.locale, loc.defaultLocale) ||
      undefined
    );
  }
  return undefined;
}

/** The string-valued entries of an object, and nothing else. */
function stringsOnly(v: object): LocalizedString {
  const out: Record<string, string> = {};
  for (const [k, value] of Object.entries(v)) {
    if (typeof value === "string") out[k] = value;
  }
  return out;
}

function firstField(
  o: Record<string, unknown>,
  keys: string[]
): string | undefined {
  for (const k of keys) {
    const v = str(o[k]);
    if (v) return v;
  }
  return undefined;
}

/**
 * The id of a thing, under any of the names an upstream uses for it.
 *
 * Not cleverness for its own sake: the content API calls it `assetId`, the
 * resolver calls it `id`, and a Mongo document carries `_id`. A tile with no id
 * cannot be pressed, so the alternative to accepting all three is a rail that
 * silently drops every item in it.
 */
function idOf(
  o: Record<string, unknown>,
  ...keys: string[]
): string | undefined {
  for (const k of [...keys, "id", "_id"]) {
    const v = str(o[k]);
    if (v) return v;
  }
  return undefined;
}

function kindOf(v: unknown): TvTitleKind {
  const raw = (str(v) ?? "").toLowerCase();
  if (raw === "movie" || raw === "film") return "movie";
  if (raw === "series" || raw === "season" || raw === "show") return "series";
  if (raw === "episode") return "episode";
  return "other";
}

/**
 * The picture for a tile.
 *
 * Upstream stores SIX image types on an array with no uniqueness constraint, so
 * "the poster" is never one row (see `@shared/vod-images`). The TV read route is
 * expected to have already chosen; this accepts whichever single name it chose
 * to send, and falls back to walking an `images` array — preferring the types a
 * tile actually wants — when it sent the array instead. A missing picture is a
 * placeholder tone, never a broken tile.
 */
function imageOf(
  o: Record<string, unknown>,
  prefer: string[],
  ...keys: string[]
): string | undefined {
  const direct = firstField(o, [
    ...keys,
    "imageUrl",
    "image",
    "posterUrl",
    "thumbnailUrl",
  ]);
  if (direct) return direct;
  const images = list(o.images).map(rec);
  for (const type of prefer) {
    const hit = images.find((img) => str(img.type) === type && str(img.url));
    if (hit) return str(hit.url);
  }
  for (const img of images) {
    const v = str(img.url);
    if (v) return v;
  }
  return undefined;
}

const TILE_IMAGE = ["thumbnail", "poster", "cover", "banner"];
const WIDE_IMAGE = ["banner", "cover", "thumbnail", "poster"];

export function normaliseItem(
  raw: unknown,
  loc: TvLocale = EN
): TvCatalogueItem | null {
  const o = rec(raw);
  const assetId = idOf(o, "assetId");
  const title =
    localizedText(o.title, loc) ??
    localizedText(o.localizedInfo, loc) ??
    str(o.originalTitle) ??
    str(o.name);
  // No id means nothing to open; no title means nothing to read at three
  // metres. Either one makes the tile useless, so it is dropped rather than
  // drawn as an empty rectangle a guest can focus and press.
  if (!assetId || !title) return null;
  return {
    assetId,
    title,
    subtitle:
      localizedText(o.subtitle, loc) ??
      localizedText(o.seriesTitle, loc) ??
      str(o.tagline),
    imageUrl: imageOf(o, TILE_IMAGE),
    kind: kindOf(o.kind ?? o.type ?? o.assetType),
    year: num(o.year ?? o.productionYear),
    durationSeconds: num(o.durationSeconds ?? o.duration),
  };
}

/**
 * Whether the server believes there is another page.
 *
 * Three envelope shapes accepted because three are plausible and the cost of
 * guessing wrong is either a rail that stops early or one that asks forever.
 * The last clause is the honest default: a page that came back empty is the end
 * of the rail, whatever the envelope said.
 */
function hasMoreFrom(o: Record<string, unknown>, itemCount: number): boolean {
  if (typeof o.hasMore === "boolean") return o.hasMore;
  const pagination = rec(o.pagination);
  if ("next" in pagination) return pagination.next != null;
  if ("next" in o) return o.next != null;
  return itemCount > 0;
}

export function normaliseRail(
  raw: unknown,
  loc: TvLocale = EN,
  fallbackPage = 1
): TvRail | null {
  const o = rec(raw);
  const carouselId = idOf(o, "carouselId");
  if (!carouselId) return null;
  const items = list(o.items ?? o.assets)
    .map((i) => normaliseItem(i, loc))
    .filter((i): i is TvCatalogueItem => i !== null);
  return {
    carouselId,
    title: localizedText(o.title, loc) ?? str(o.name) ?? "",
    items,
    page: num(o.page ?? rec(o.pagination).page) ?? fallbackPage,
    hasMore: hasMoreFrom(o, items.length),
  };
}

export function normaliseHome(
  raw: unknown,
  loc: TvLocale = EN
): TvCatalogueHome {
  const o = rec(raw);
  const empty = rec(o.emptyState);
  const code = str(empty.code);
  return {
    rails: list(o.rails ?? o.carousels)
      .map((r) => normaliseRail(r, loc))
      .filter((r): r is TvRail => r !== null),
    subPages: normaliseSubPages(o.subPages ?? o.subpages, loc),
    ...(code
      ? {
          emptyState: {
            code,
            hint: str(empty.hint) ?? "",
            authoredRails: num(empty.authoredRails),
            visibleRails: num(empty.visibleRails),
            danglingRails: num(empty.danglingRails),
          },
        }
      : {}),
  };
}

export function normaliseSubPages(
  raw: unknown,
  loc: TvLocale = EN
): TvSubPage[] {
  const source = Array.isArray(raw)
    ? raw
    : list(rec(raw).subPages ?? rec(raw).subpages);
  return source
    .map((entry) => {
      const o = rec(entry);
      const subPageId = idOf(o, "subPageId", "subpageId");
      const title = localizedText(o.title, loc) ?? str(o.name);
      if (!subPageId || !title) return null;
      return {
        subPageId,
        title,
        icon: str(o.icon) ?? null,
        // Reuses the home normaliser: a rail is a rail, and two spellings of
        // one shape is how the two drift apart.
        rails: list(o.rails ?? o.carousels)
          .map((r) => normaliseRail(r, loc))
          .filter((r): r is TvRail => r !== null),
      };
    })
    .filter((s): s is TvSubPage => s !== null);
}

function normaliseEpisode(
  raw: unknown,
  loc: TvLocale,
  seasonNumber?: number
): TvEpisode | null {
  const base = normaliseItem(raw, loc);
  if (!base) return null;
  const o = rec(raw);
  return {
    ...base,
    kind: base.kind === "other" ? "episode" : base.kind,
    episodeNumber: num(o.episodeNumber ?? o.episode ?? o.number),
    seasonNumber: num(o.seasonNumber ?? o.season) ?? seasonNumber,
  };
}

/** A credit only when it has a name; its optional lines only when non-empty. */
function creditOf(raw: unknown): TvTitleDetail["credit"] {
  const c = rec(raw);
  const name = str(c.name);
  if (!name) return undefined;
  const credits = str(c.credits);
  const copyright = str(c.copyright);
  return {
    name,
    ...(credits ? { credits } : {}),
    ...(copyright ? { copyright } : {}),
  };
}

export function normaliseTitle(
  raw: unknown,
  loc: TvLocale = EN
): TvTitleDetail | null {
  const o = rec(raw);
  const assetId = idOf(o, "assetId");
  const title =
    localizedText(o.title, loc) ??
    localizedText(o.localizedInfo, loc) ??
    str(o.originalTitle) ??
    str(o.name);
  if (!assetId || !title) return null;
  const seasons: TvSeason[] = list(o.seasons)
    .map((entry, i) => {
      const s = rec(entry);
      const number = num(s.number ?? s.seasonNumber);
      const episodes = list(s.episodes)
        .map((e) => normaliseEpisode(e, loc, number))
        .filter((e): e is TvEpisode => e !== null);
      return {
        seasonId: idOf(s, "seasonId") ?? `season-${number ?? i + 1}`,
        number,
        title: localizedText(s.title, loc) ?? str(s.name),
        episodes,
      };
    })
    // A season with no episodes behind it is a heading with nothing in it —
    // focusable, pressable, empty. Dropped rather than left as a dead chip.
    .filter((s) => s.episodes.length > 0);
  // A catalogue that sends episodes without wrapping them in a season still
  // gets a series tree, because the picker is built on seasons and a flat list
  // would otherwise be invisible.
  const flat = list(o.episodes)
    .map((e) => normaliseEpisode(e, loc))
    .filter((e): e is TvEpisode => e !== null);
  if (seasons.length === 0 && flat.length > 0) {
    seasons.push({ seasonId: "season-1", episodes: flat });
  }
  const kind = kindOf(o.kind ?? o.type ?? o.assetType);
  return {
    assetId,
    title,
    // The tree is a more reliable witness than the label: anything with seasons
    // behind it IS a series whatever it called itself, and getting this wrong
    // means the episode picker never opens.
    kind: seasons.length > 0 ? "series" : kind,
    synopsis:
      localizedText(o.synopsis, loc) ??
      localizedText(o.description, loc) ??
      localizedText(o.localizedInfo, loc, "synopsisMedium", "synopsisLong") ??
      str(o.shortDescription),
    imageUrl: imageOf(o, TILE_IMAGE),
    backdropUrl: imageOf(o, WIDE_IMAGE, "backdropUrl", "bannerUrl", "coverUrl"),
    year: num(o.year ?? o.productionYear),
    durationSeconds: num(o.durationSeconds ?? o.duration),
    genres: list(o.genres)
      .map(
        (g) =>
          str(g) ?? localizedText(rec(g).localizedName, loc) ?? str(rec(g).name)
      )
      .filter((g): g is string => !!g),
    rating: str(o.rating) ?? str(o.parentalRating),
    seasons,
    credit: creditOf(o.credit),
  };
}

// ── the wire ────────────────────────────────────────────────────────────────

/**
 * The screen-token headers every premise-scoped read carries.
 *
 * Exported because the playback resolve in `_shared/vod/vodPlayback.ts` needs
 * exactly the same credential, and a second copy of this is how one of them
 * ends up sending an anonymous request the day the header is renamed.
 */
export function screenAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = getBinding()?.screenToken;
  if (token) headers["X-Screen-Token"] = token;
  return headers;
}

/** `GET /api/foyer/screens/:screenId/tv/<path>`, parsed, or {@link TvApiError}. */
export async function tvScreenGet(
  path: string,
  init?: RequestInit
): Promise<unknown> {
  const res = await fetch(`/api/foyer/screens/${getScreenId()}/tv/${path}`, {
    ...init,
    headers: { ...screenAuthHeaders(), ...(init?.headers ?? {}) },
  });
  if (!res.ok) throw new TvApiError(res.status);
  return res.json();
}

/**
 * How long a read a guest is WAITING ON may take before it is called a failure.
 *
 * ⚠️ Every read needs one. A hotel's captive portal accepts the connection and
 * then never answers, and `fetch` has no timeout of its own — so without this a
 * guest who pressed OK on a title watches "Hämtar…" until somebody reboots the
 * panel.
 *
 * 🚨 This note used to say the home read did not need a bound, because "its
 * poll and backoff already bound the wait". **That was wrong**, and it was the
 * same wrong reasoning in four other hooks. A backoff only ever schedules the
 * attempt *after* the current one settles — so a request that never settles
 * never reaches the `catch`, never reschedules, and silently ends the poll for
 * the lifetime of the mount. The bound is what turns that permanent hang back
 * into an ordinary retryable failure. See `../lib/boundedFetch`.
 */
export const READ_TIMEOUT_MS = FETCH_TIMEOUT_MS;

/** {@link tvScreenGet}, but it gives up rather than hanging. */
async function tvScreenGetBounded(
  path: string,
  timeoutMs = READ_TIMEOUT_MS
): Promise<unknown> {
  const { signal, done } = deadline(timeoutMs);
  try {
    return await tvScreenGet(path, signal ? { signal } : undefined);
  } finally {
    done();
  }
}

/**
 * The named sub-pages of the library ("Barn", "Nyheter").
 *
 * A separate read because it is a separate route and a separate cadence — and
 * because {@link useTvCatalogue} deliberately does not fold it into the home
 * poll: a screen that shows rails does not need the sub-page list to render its
 * first frame, and making it wait on one would put a second round trip in front
 * of the library.
 */
export async function fetchTvSubPages(loc: TvLocale): Promise<TvSubPage[]> {
  return normaliseSubPages(await tvScreenGetBounded("subpages"), loc);
}

/**
 * One page of one rail.
 *
 * ⚠️ **An empty FIRST page means the rail is unavailable; an empty LATER page
 * means the end was reached.** They are the same JSON and they must not produce
 * the same screen — "nothing here" printed under a rail a guest has just
 * arrowed to the end of is a bug report waiting to be filed. The distinction is
 * made by the caller, which knows which page it asked for; this function only
 * reports what came back.
 */
export async function fetchTvRailPage(
  carouselId: string,
  page: number,
  loc: TvLocale
): Promise<TvRail | null> {
  const raw = await tvScreenGetBounded(
    `rail/${encodeURIComponent(carouselId)}?page=${page}`
  );
  // The envelope may omit the id it was asked about; filling it back in beats
  // dropping a page that is otherwise perfectly good.
  return normaliseRail({ carouselId, ...rec(raw) }, loc, page);
}

export async function fetchTvTitle(
  assetId: string,
  loc: TvLocale
): Promise<TvTitleDetail | null> {
  return normaliseTitle(
    await tvScreenGetBounded(`title/${encodeURIComponent(assetId)}`),
    loc
  );
}

// ── the hook ────────────────────────────────────────────────────────────────

/**
 * What the screen is holding, and whether it is still hoping.
 *
 * `home` keeps `useRoomOffers`' contract exactly: **`undefined` means "not
 * answered yet"**, and an answered-but-empty library is `{rails: [], …}`. That
 * distinction is what stops a Film & TV tile appearing and then vanishing, and
 * it is why the empty case is a value rather than a null.
 *
 * `unreachable` is the third state `useRoomOffers` never needed. Offers live
 * behind a tile that simply stays hidden; this screen has already been ENTERED
 * by the time the read fails, and a guest standing in front of a spinner that
 * will never resolve is the exact failure mode this whole player exists to
 * remove. So the read says when it has stopped hoping, the screen says so in
 * words, and the retries carry on underneath regardless.
 */
export interface TvCatalogueRead {
  home: TvCatalogueHome | undefined;
  unreachable: boolean;
  /** Ask again now — what a "Try again" press does. */
  reload: () => void;
}

/** One stable instance: an effect watches this value, and a fresh object every
 *  render would re-run it forever. */
const EMPTY_HOME: TvCatalogueHome = { rails: [], subPages: [] };

/**
 * The home read for a premise.
 *
 * A failed request keeps the LAST GOOD library rather than blanking the rails —
 * the same instinct `useConfig` has about the cached config. A hotel Wi-Fi blip
 * must not empty a guest's library mid-browse.
 *
 * `preview` short-circuits the whole thing for the CMS template editor, which
 * has no pairing and therefore no screen token. A bound television is never
 * handed one.
 */
export function useTvCatalogue(
  premiseId: string | undefined,
  loc: TvLocale,
  preview?: TvCatalogueHome
): TvCatalogueRead {
  // The RAW body is what is held, and the locale is applied on the way out.
  //
  // Deliberate: a guest switching interface language must re-LABEL the library
  // they are looking at, not re-fetch it — and the fetch effect must therefore
  // not have the locale among its dependencies, or every switch would blank the
  // rails and re-run the whole backoff. Parsing is cheap; a round trip on a
  // hotel uplink is not.
  const [raw, setRaw] = useState<unknown>(undefined);
  const [answeredOnce, setAnsweredOnce] = useState(false);
  const [unreachable, setUnreachable] = useState(false);
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => {
    setUnreachable(false);
    setNonce((n) => n + 1);
  }, []);

  // Cleared on a premise change for the reason `useRoomOffers` gives: the CMS
  // preview keeps one iframe mounted across a switch, so the previous hotel's
  // library would otherwise keep rendering as if it were this one's.
  useEffect(() => {
    setRaw(undefined);
    setAnsweredOnce(false);
    setUnreachable(false);
    if (preview || !premiseId) return;
    let alive = true;
    let failures = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const schedule = (ms: number) => {
      timer = setTimeout(() => {
        timer = undefined;
        void load();
      }, ms);
    };

    async function load(): Promise<void> {
      try {
        // Bounded like every other read: an unanswered request would leave
        // this promise pending, skipping the `catch` that reschedules the
        // poll — which would stop the home feed refreshing for good.
        const next = await tvScreenGetBounded("home");
        if (!alive) return;
        // A 200 that is not a home read is the network lying, not the hotel
        // answering. Treated as a failure so the backoff keeps trying and the
        // screen keeps whatever it already had — see `looksLikeHome`.
        if (!looksLikeHome(next)) throw new Error("not a home read");
        failures = 0;
        setUnreachable(false);
        setAnsweredOnce(true);
        setRaw(next);
        schedule(TV_CATALOGUE_POLL_MS);
      } catch {
        if (!alive) return;
        failures += 1;
        if (failures >= UNREACHABLE_AFTER) setUnreachable(true);
        schedule(Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** (failures - 1)));
      }
    }

    void load();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [premiseId, preview, nonce]);

  const { locale, defaultLocale } = loc;
  const parsed = useMemo(
    () =>
      answeredOnce ? normaliseHome(raw, { locale, defaultLocale }) : undefined,
    [raw, answeredOnce, locale, defaultLocale]
  );
  const answered = preview ?? parsed;
  // A screen that was never bound to a premise is not "loading" — there is
  // nobody to ask. Saying so at once is what keeps an unpaired panel and the
  // CMS preview off the forever-spinner.
  const noPremise = !preview && !premiseId;
  return {
    home: noPremise ? EMPTY_HOME : answered,
    unreachable: unreachable && !answered,
    reload,
  };
}

/**
 * Has the library answered, and answered with nothing?
 *
 * Every rail empty is the same screen as no rails at all: a premise with rails
 * authored but no package granted is exactly the production state today, and a
 * column of named headings with nothing under them reads as a broken television
 * rather than as an empty catalogue.
 */
export function isEmptyCatalogue(home: TvCatalogueHome | undefined): boolean {
  return !!home && home.rails.every((r) => r.items.length === 0);
}

/**
 * Does this body look like a home read at all?
 *
 * The guard `useRoomOffers` learned to need: **a 200 without the array in it is
 * a proxy or a captive portal answering for the API, not a hotel with nothing to
 * watch.** Without this, a hotel Wi-Fi login page would present as "no film
 * library is connected to this hotel" — a sentence about the contract, printed
 * because of a network appliance — and the retry loop would stop, because the
 * read would believe it had been answered.
 *
 * Deliberately loose: the array under EITHER name, and nothing about its
 * contents. An empty `rails: []` is a real and expected answer.
 */
export function looksLikeHome(raw: unknown): boolean {
  const o = rec(raw);
  return Array.isArray(o.rails) || Array.isArray(o.carousels);
}
